// deps
import React, { memo } from "react";

// internal deps
import { Portal, format } from "@swiggy-private/core-ui";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { default as ClearLightIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/clearLight/24";
import { default as LockFilledSmoothIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/lockFilledSmooth/12";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";
import { VEG_TYPE, ItemPresentationAttributes } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";

// widgets
import { FoodSymbolIconSvg } from "@cart-ui/widgets/food-symbol-icon";

// helpers
import { priceInRupee } from "@cart-ui/helpers/Price";
import { addWordBreaks, resolveFlipTextWithItemValues } from "./utils";

// constants
import { CROSS_SELL_M2_ITEM_PLACEHOLDER, TICKER_REPEAT_COUNT } from "@cart-ui/constants";
import {
    LINE_FOR_POPUP_IMAGE_ID,
    UNLOCK_POPUP_MESSAGE,
    EXPLORE_ITEMS_FROM_MENU,
} from "@cart-ui/components/cross-sell-m2-carousel/constants";

// styles
import {
    PopupWidth,
    UnlockPopupContainer,
    UnlockPopupCloseIcon,
    UnlockPopupContentContainer,
    UnlockPopupFoodIconContainer,
    UnlockPopupDetails,
    UnlockPopupFlexContainer,
    UnlockPopupBasePriceContainer,
    UnlockPopupFinalPriceContainer,
    UnlockPopupItemCardContainer,
    UnlockPopupImageContainer,
    UnlockPopupTextContainer,
    UnlockPopupTickerWrapper,
    UnlockPopupTickerContent,
    UnlockPopupTickerItem,
    UnlockPopupName,
    UnlockPopupButtonContainer,
    UnlockPopupDecorativeLine,
    UnlockMessageText,
    UnlockPopupLockIconContainer,
} from "./UnlockPopup.style";

const IMAGE_WIDTH = "75";
const CLOUDINARY_HEIGHT = 144;

export interface UnlockPopupProps {
    isOpen: boolean;
    onClose: () => void;
    itemName: string;
    unlockAmount?: number;
    onExploreClick?: () => void;
    // Add these props for the item card
    imageId?: string;
    vegType?: VEG_TYPE;
    basePrice?: number;
    finalPrice?: number;
    showPriceDrop?: boolean;
    onImpressionMagicItemsUnlockPopup?: () => void;
    onClickDismissMagicItemsUnlockPopup?: () => void;
    onClickExploreMenuMagicItems?: () => void;
    itemPresentationAttributes?: ItemPresentationAttributes;
    /** Per-item discount %; substituted into flip_text template {item_discount_percentage} */
    itemDiscountPercentage?: number;
}

const UnlockPopup: React.FC<UnlockPopupProps> = ({
    isOpen,
    onClose,
    itemName,
    unlockAmount,
    onExploreClick,
    imageId,
    vegType = VEG_TYPE.VEG,
    basePrice,
    finalPrice,
    showPriceDrop: _showPriceDrop = false,
    onImpressionMagicItemsUnlockPopup,
    onClickDismissMagicItemsUnlockPopup,
    onClickExploreMenuMagicItems,
    itemPresentationAttributes,
    itemDiscountPercentage,
}) => {
    const theme = useTheme();
    const itemNameRef = React.useRef<HTMLSpanElement>(null);

    // Resolve flip text with per-item
    const flipTextItems = React.useMemo(
        () => resolveFlipTextWithItemValues(itemPresentationAttributes?.flip_text, itemDiscountPercentage),
        [itemPresentationAttributes?.flip_text, itemDiscountPercentage],
    );

    // Fire impression event when popup first appears
    React.useEffect(() => {
        if (isOpen && onImpressionMagicItemsUnlockPopup) {
            onImpressionMagicItemsUnlockPopup();
        }
    }, [isOpen, onImpressionMagicItemsUnlockPopup]);

    // Format item name with word breaks (same as CrossSellM2Item)
    React.useEffect(() => {
        const formattedName = addWordBreaks(itemName, false);
        if (!isEmpty(itemNameRef.current)) {
            itemNameRef.current.innerHTML = formattedName;
        }
    }, [itemName]);

    const handleClose = () => {
        onClickDismissMagicItemsUnlockPopup?.();
        onClose();
    };

    const handleExploreClick = () => {
        onClickExploreMenuMagicItems?.();
        onExploreClick?.();
        onClose();
    };

    return (
        <Portal>
            <UnlockPopupContainer
                isOpen={isOpen}
                closeOnOverlayClick
                onClose={handleClose}
                hasCustomPadding
                width={PopupWidth}
            >
                <UnlockPopupCloseIcon onClick={handleClose}>
                    <ClearLightIcon />
                </UnlockPopupCloseIcon>

                <UnlockPopupContentContainer>
                    {/* Horizontal Item Card - using same structure as CrossSellM2Item */}
                    <UnlockPopupItemCardContainer>
                        <UnlockPopupImageContainer>
                            <Image
                                cloudinaryId={imageId}
                                errorFallbackSrc={CROSS_SELL_M2_ITEM_PLACEHOLDER}
                                fit="cover"
                                width={IMAGE_WIDTH}
                                cloudinaryHeight={CLOUDINARY_HEIGHT}
                            />
                            <UnlockPopupLockIconContainer>
                                <LockFilledSmoothIcon fill={theme.colors[theme.colorMode].text_Low_Emphasis} />
                            </UnlockPopupLockIconContainer>
                        </UnlockPopupImageContainer>

                        <UnlockPopupTextContainer>
                            <UnlockPopupDetails>
                                <UnlockPopupName
                                    variant="V2.body3.neutral.regular"
                                    color="text_High_Emphasis"
                                    maxLines={2}
                                >
                                    <UnlockPopupFoodIconContainer>
                                        <FoodSymbolIconSvg vegType={vegType} />
                                    </UnlockPopupFoodIconContainer>
                                    <span ref={itemNameRef}></span>
                                </UnlockPopupName>
                            </UnlockPopupDetails>

                            {!isEmpty(flipTextItems) && (
                                <UnlockPopupTickerWrapper>
                                    <UnlockPopupTickerContent shouldAnimate={flipTextItems.length > 1}>
                                        {flipTextItems.length > 1 ? (
                                            // Repeat items for continuous scroll effect when multiple items
                                            [...Array(TICKER_REPEAT_COUNT)].map((_, repeatIndex) => (
                                                <React.Fragment key={repeatIndex}>
                                                    {flipTextItems.map((item, index) => (
                                                        <UnlockPopupTickerItem
                                                            key={`${repeatIndex}-${index}`}
                                                            variant="V2.caption.neutral.bold"
                                                            color={item.color}
                                                        >
                                                            {item.text}
                                                        </UnlockPopupTickerItem>
                                                    ))}
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            // Single item - display statically
                                            <UnlockPopupTickerItem
                                                variant="V2.caption.neutral.bold"
                                                color={flipTextItems[0].color}
                                            >
                                                {flipTextItems[0].text}
                                            </UnlockPopupTickerItem>
                                        )}
                                    </UnlockPopupTickerContent>
                                </UnlockPopupTickerWrapper>
                            )}

                            <UnlockPopupFlexContainer>
                                <UnlockPopupFinalPriceContainer
                                    variant="V2.overline.neutral.bold"
                                    color="text_High_Emphasis"
                                >
                                    {`₹${priceInRupee(finalPrice || 0)}`}
                                </UnlockPopupFinalPriceContainer>
                                {basePrice !== finalPrice && (
                                    <UnlockPopupBasePriceContainer
                                        variant="V2.caption.neutral.medium"
                                        color="text_Low_Emphasis"
                                    >
                                        {`₹${priceInRupee(basePrice || 0)}`}
                                    </UnlockPopupBasePriceContainer>
                                )}
                            </UnlockPopupFlexContainer>
                        </UnlockPopupTextContainer>
                    </UnlockPopupItemCardContainer>

                    <UnlockPopupDecorativeLine>
                        <Image cloudinaryId={LINE_FOR_POPUP_IMAGE_ID} />
                    </UnlockPopupDecorativeLine>

                    <UnlockMessageText variant="V2.body1.neutral.bold" color="text_High_Emphasis">
                        {format(UNLOCK_POPUP_MESSAGE, String(unlockAmount))}
                    </UnlockMessageText>

                    <UnlockPopupButtonContainer onClick={handleExploreClick}>
                        <Text variant="V2.body1.neutral.bold" color="text_inverse_Highest_Emphasis">
                            {EXPLORE_ITEMS_FROM_MENU}
                        </Text>
                    </UnlockPopupButtonContainer>
                </UnlockPopupContentContainer>
            </UnlockPopupContainer>
        </Portal>
    );
};

export default memo(UnlockPopup);
